import { booleanAttribute, inject, ChangeDetectorRef, ElementRef, numberAttribute } from '@angular/core';

/** Mixin to include a `disabled` property. */
function mixinDisabled(base) {
  return class extends base {
    get disabled() {
      return this._disabled;
    }
    set disabled(value) {
      this._disabled = booleanAttribute(value);
    }
    // eslint-disable-next-line
    constructor(...args) {
      super(...args);
      this._changeDetector = inject(ChangeDetectorRef);
      this._disabled = false;
    }
    /** @internal */
    setDisabledState(isDisabled) {
      this.disabled = isDisabled;
      this._changeDetector.markForCheck();
    }
  };
}
function mixinInputA11y(base) {
  return class extends base {
    /**
     * Specify the `aria-label` value which can be added to the input element
     */
    get ariaLabel() {
      return this._ariaLabel;
    }
    set ariaLabel(ariaLabel) {
      this._elementRef.nativeElement?.removeAttribute('aria-label');
      this._ariaLabel = ariaLabel;
    }
    /**
     * Specify the `aria-labelledby` value which can be added to the input element
     */
    get ariaLabelledby() {
      return this._ariaLabelledby;
    }
    set ariaLabelledby(ariaLabelledby) {
      this._elementRef.nativeElement?.removeAttribute('aria-labelledby');
      this._ariaLabelledby = ariaLabelledby;
    }
    /**
     * Specify the `aria-describedby` value which can be added to the input element
     */
    get ariaDescribedby() {
      return this._ariaDescribedby;
    }
    set ariaDescribedby(ariaDescribedby) {
      this._elementRef.nativeElement?.removeAttribute('aria-describedby');
      this._ariaDescribedby = ariaDescribedby;
    }
    /**
     * Specify the `name` value which can be added to the input element
     */
    get name() {
      return this._name;
    }
    set name(name) {
      this._elementRef.nativeElement?.removeAttribute('name');
      this._name = name;
    }
    // eslint-disable-next-line
    constructor(...args) {
      super(...args);
      this._elementRef = inject(ElementRef);
      this.ariaLabel = this._elementRef.nativeElement.getAttribute('aria-label');
      this.ariaLabelledby = this._elementRef.nativeElement.getAttribute('aria-labelledby');
      this.ariaDescribedby = this._elementRef.nativeElement.getAttribute('aria-describedby');
      this.name = this._elementRef.nativeElement.getAttribute('name');
    }
  };
}

/** Mixin to augment a directive with a `size` property. */
function mixinSize(base) {
  return class extends base {
    /** The size of the component. */
    get size() {
      return this._size;
    }
    /** The size of the component. */
    set size(value) {
      const size = value;
      if (size !== this._size) {
        if (this._size) {
          this._elementRef.nativeElement.classList.remove(`uxa-${this._size}`);
        }
        if (size) {
          this._elementRef.nativeElement.classList.add(`uxa-${size}`);
        }
        this._size = size;
      }
    }
    // eslint-disable-next-line
    constructor(...args) {
      super(...args);
      this._elementRef = inject(ElementRef);
    }
  };
}

/** Mixin to include a `tabindex` property. */
function mixinTabIndex(base, defaultTabIndex = 0) {
  return class extends base {
    /** Get the tabindex of the component */
    get tabIndex() {
      return this.disabled ? -1 : this._tabIndex;
    }
    /** Set the tabindex of the component */
    set tabIndex(value) {
      this._tabIndex = Number.isInteger(value) ? numberAttribute(value) : this.defaultTabIndex;
    }
    // eslint-disable-next-line
    constructor(...args) {
      super(...args);
      this._tabIndex = defaultTabIndex;
      this.defaultTabIndex = defaultTabIndex;
      // Set the default tabindex that can be specified from the mixin.
      this._tabIndex = this.defaultTabIndex;
    }
  };
}

/** Mixin to augment a directive with a `variant` property. */
function mixinVariant(base, defaultVariant) {
  return class extends base {
    /** Appearance variant for the component. */
    get variant() {
      return this._variant;
    }
    /** Appearance variant for the component. */
    set variant(value) {
      const variant = value || this.defaultVariant;
      if (variant !== this._variant) {
        if (this._variant) {
          this._elementRef.nativeElement.classList.remove(`uxa-${this._variant}`);
        }
        if (variant) {
          this._elementRef.nativeElement.classList.add(`uxa-${variant}`);
        }
        this._variant = variant;
      }
    }
    // eslint-disable-next-line
    constructor(...args) {
      super(...args);
      this._elementRef = inject(ElementRef);
      this.defaultVariant = defaultVariant;
      // Set the default variant that can be specified from the mixin.
      this.variant = defaultVariant;
    }
  };
}

/**
 * Generated bundle index. Do not edit.
 */

export { mixinDisabled, mixinInputA11y, mixinSize, mixinTabIndex, mixinVariant };
